import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import ApiService from "@/services/api";

interface StatutoryReport {
  report_id: string;
  report_type: string;
  company_name: string;
  tax_year: string;
  report_period_start: string;
  report_period_end: string;
  total_employees: number | null;
  total_gross_pay: string | null;
  total_paye: string | null;
  total_ni_employee: string | null;
  total_ni_employer: string | null;
  parsing_confidence: number;
  parsing_notes: string | null;
  employee_count: number;
  created_at: string;
}

interface StatutoryReportsCardProps {
  sessionId: string;
}

export const StatutoryReportsCard: React.FC<StatutoryReportsCardProps> = ({
  sessionId,
}) => {
  const [reports, setReports] = useState<StatutoryReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedReports, setExpandedReports] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    const fetchStatutoryReports = async () => {
      try {
        const response = await ApiService.request<{
          reports: StatutoryReport[];
        }>("getStatutoryReports", {
          urlParams: { sessionId },
          skipAuth: true,
        });
        setReports(response.reports || []);
      } catch (error) {
        console.error("Error fetching statutory reports:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatutoryReports();
  }, [sessionId]);

  const toggleReport = (reportId: string) => {
    setExpandedReports((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  const getReportTypeLabel = (reportType: string): string => {
    const labels: Record<string, string> = {
      P11_PAYE: "P11 PAYE",
      P11_NIC: "P11 NIC",
      P32: "P32 Summary",
      FPS: "Full Payment Submission",
      EPS: "Employer Payment Summary",
      PAYROLL_ACTIVITY: "Payroll Activity",
    };
    return labels[reportType] || reportType;
  };

  const getReportTypeColor = (reportType: string): string => {
    const colors: Record<string, string> = {
      P11_PAYE: "bg-blue-100 text-blue-800",
      P11_NIC: "bg-green-100 text-green-800",
      P32: "bg-purple-100 text-purple-800",
      FPS: "bg-orange-100 text-orange-800",
      EPS: "bg-pink-100 text-pink-800",
      PAYROLL_ACTIVITY: "bg-gray-100 text-gray-800",
    };
    return colors[reportType] || "bg-gray-100 text-gray-800";
  };

  const formatCurrency = (value: string | null): string => {
    if (!value) return "N/A";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(parseFloat(value));
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Statutory Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading reports...</p>
        </CardContent>
      </Card>
    );
  }

  if (reports.length === 0) {
    return null; // Don't show card if no statutory reports
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Statutory Reports Detected
        </CardTitle>
        <CardDescription>
          {reports.length} statutory report{reports.length !== 1 ? "s" : ""}{" "}
          parsed from uploaded documents
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {reports.map((report) => (
            <div
              key={report.report_id}
              className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
            >
              <div
                className="flex items-start justify-between cursor-pointer"
                onClick={() => toggleReport(report.report_id)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge
                      variant="secondary"
                      className={getReportTypeColor(report.report_type)}
                    >
                      {getReportTypeLabel(report.report_type)}
                    </Badge>
                    {report.parsing_confidence >= 0.9 ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    )}
                    <span className="text-xs text-muted-foreground">
                      {Math.round(report.parsing_confidence * 100)}%
                      confidence
                    </span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {report.company_name || "Unknown Company"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Tax Year: {report.tax_year || "N/A"}
                    </p>
                    {report.employee_count > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {report.employee_count} employee
                        {report.employee_count !== 1 ? "s" : ""} extracted
                      </p>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="sm">
                  {expandedReports.has(report.report_id) ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {expandedReports.has(report.report_id) && (
                <div className="mt-4">
                  <div className="border-t pt-4 space-y-3">
                    {/* Report Period */}
                    {report.report_period_start && report.report_period_end && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          Report Period
                        </p>
                        <p className="text-sm">
                          {formatDate(report.report_period_start)} -{" "}
                          {formatDate(report.report_period_end)}
                        </p>
                      </div>
                    )}

                    {/* Company-level totals (for P32) */}
                    {report.report_type === "P32" && (
                      <div className="grid grid-cols-2 gap-4">
                        {report.total_employees !== null && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              Total Employees
                            </p>
                            <p className="text-sm font-semibold">
                              {report.total_employees}
                            </p>
                          </div>
                        )}
                        {report.total_gross_pay && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              Total Gross Pay
                            </p>
                            <p className="text-sm font-semibold">
                              {formatCurrency(report.total_gross_pay)}
                            </p>
                          </div>
                        )}
                        {report.total_paye && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              Total PAYE
                            </p>
                            <p className="text-sm font-semibold">
                              {formatCurrency(report.total_paye)}
                            </p>
                          </div>
                        )}
                        {report.total_ni_employee && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              Total Employee NI
                            </p>
                            <p className="text-sm font-semibold">
                              {formatCurrency(report.total_ni_employee)}
                            </p>
                          </div>
                        )}
                        {report.total_ni_employer && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              Total Employer NI
                            </p>
                            <p className="text-sm font-semibold">
                              {formatCurrency(report.total_ni_employer)}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Parsing notes */}
                    {report.parsing_notes && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          Parsing Notes
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {report.parsing_notes}
                        </p>
                      </div>
                    )}

                    {/* Low confidence warning */}
                    {report.parsing_confidence < 0.9 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                        <div className="flex gap-2">
                          <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <div className="text-xs text-yellow-800">
                            <p className="font-medium mb-1">
                              Low Confidence Parsing
                            </p>
                            <p>
                              The AI had some uncertainty parsing this report.
                              Please verify the extracted data manually.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Summary info */}
        <div className="mt-4 pt-4 border-t">
          <p className="text-xs text-muted-foreground">
            Statutory reports are automatically cross-referenced with your CSV
            payroll data. Any discrepancies are flagged as validation issues
            above.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

